import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Check, Download, Loader2, Package, AlertCircle } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useCart } from "../contexts/CartContext";
import BrandingFooter from "../components/BrandingFooter";
import FloatingBrandingBadge from "../components/FloatingBrandingBadge";

const DownloadSuccess = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("loading");
  const [order, setOrder] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  
  // Clear cart on successful purchase
  const { clearCart } = useCart();

  const pollOrderStatus = async () => {
    if (!orderId) {
      setStatus("error");
      return;
    }

    try {
      // Pass session_id so backend can verify payment with Stripe
      const params = sessionId ? `?session_id=${sessionId}` : "";
      const response = await axios.get(`${API}/downloads/order/${orderId}${params}`);
      const orderData = response.data;
      
      if (orderData.status === "completed" || orderData.status === "paid") {
        setOrder(orderData);
        setStatus("success");
        // Clear the cart since purchase was successful
        clearCart();
      } else if (orderData.status === "pending") {
        // Still processing - continue polling
        if (pollCount < 15) {
          setTimeout(() => {
            setPollCount(prev => prev + 1);
          }, 2000);
        } else {
          // After 30 seconds, show pending state
          setOrder(orderData);
          setStatus("pending");
        }
      } else if (orderData.status === "failed" || orderData.status === "expired") {
        setStatus("failed");
      } else {
        // Unknown status - show order details anyway
        setOrder(orderData);
        setStatus("success");
      }
    } catch (error) {
      console.error("Order status error:", error);
      if (pollCount < 5) {
        setTimeout(() => {
          setPollCount(prev => prev + 1);
        }, 2000);
      } else {
        setStatus("error");
      }
    }
  };

  useEffect(() => {
    pollOrderStatus();
  }, [pollCount]);

  const handleDownload = async () => {
    if (!orderId || !order?.download_token) {
      toast.error("Download link not available yet. Please check your email.");
      return;
    }
    
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/downloads/order/${orderId}/files?token=${order.download_token}`,
        { responseType: "blob" }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Get filename from header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "photos.zip";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download files. Please check your email for the download link.");
    } finally {
      setDownloading(false);
    }
  };

  // Get photographer info for branding
  const photographer = order?.photographer;
  const photographerName = photographer?.company_name || photographer?.user_name || "360Lumina";
  const photographerSlug = photographer?.slug;
  const photographerPlanKey = photographer?.plan_key || "starter";
  const logoUrl = photographer?.logo_url ? `${API.replace("/api", "")}${photographer.logo_url}` : null;
  
  // Determine home link - go to photographer's landing page, not 360Lumina
  const getHomeLink = () => {
    if (photographerSlug) {
      return `/photographer/${photographerSlug.replace(/\s+/g, '_')}`;
    }
    // Fallback to gallery if we have it
    if (order?.gallery_id) {
      return `/galleries/${order.gallery_id}`;
    }
    return "/galleries";
  };

  return (
    <div className="min-h-screen py-12 px-6 flex flex-col" data-testid="download-success">
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-lg w-full">
          {/* Header - Show photographer branding */}
          <div className="text-center mb-8">
            <Link to={getHomeLink()} className="inline-flex items-center gap-3 mb-6">
              {logoUrl ? (
                <img src={logoUrl} alt={photographerName} className="h-10 max-w-[180px] object-contain" />
              ) : (
                <>
                  <Camera className="w-10 h-10 text-primary" />
                  <span className="text-2xl font-serif">{photographerName}</span>
                </>
              )}
            </Link>
          </div>

          <Card className="bg-card border-border">
            <CardContent className="p-8">
              {/* Loading State */}
              {status === "loading" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center"
                >
                  <Loader2 className="w-16 h-16 mx-auto mb-6 text-primary animate-spin" />
                  <h2 className="text-2xl font-serif mb-2">Processing Your Order</h2>
                  <p className="text-muted-foreground">
                    Please wait while we confirm your purchase...
                  </p>
                </motion.div>
              )}

              {/* Success State */}
              {status === "success" && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-10 h-10 text-green-400" />
                    </div>
                    <h2 className="text-2xl font-serif mb-2">Purchase Complete!</h2>
                    <p className="text-muted-foreground">
                      Thank you for your purchase. Your photos are ready to download.
                    </p>
                  </div>

                  {/* Order Summary */}
                  {order && (
                    <div className="bg-muted/30 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-muted-foreground">Order ID</span>
                        <span className="text-sm font-mono">{order.order_id}</span>
                      </div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-muted-foreground">Items</span>
                        <span className="text-sm">
                          {order.items?.length || order.photo_count || "N/A"} photo{(order.items?.length || order.photo_count) !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Total</span>
                        <span className="font-serif text-lg">${(order.total_amount || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Download Button - only show if download token is available */}
                  {order?.download_token ? (
                    <Button
                      onClick={handleDownload}
                      disabled={downloading}
                      className="w-full btn-primary mb-3"
                      size="lg"
                      data-testid="download-photos-btn"
                    >
                      {downloading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Preparing Download...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Download Your Photos
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="bg-muted/30 rounded-lg p-4 mb-3 text-center">
                      <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">
                        Preparing your download...
                      </p>
                    </div>
                  )}

                  <p className="text-xs text-center text-muted-foreground mb-6">
                    A download link will also be sent to your email.
                  </p>

                  <div className="flex gap-3">
                    {order?.gallery_id ? (
                      <Link to={`/galleries/${order.gallery_id}`} className="flex-1">
                        <Button variant="outline" className="w-full">
                          Back to Gallery
                        </Button>
                      </Link>
                    ) : (
                      <Link to={getHomeLink()} className="flex-1">
                        <Button variant="outline" className="w-full">
                          Continue Browsing
                        </Button>
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Pending State */}
              {status === "pending" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Package className="w-8 h-8 text-yellow-400" />
                  </div>
                  <h2 className="text-2xl font-serif mb-2">Processing Order</h2>
                  <p className="text-muted-foreground mb-6">
                    Your payment was received and your order is being processed. 
                    You will receive an email with download links shortly.
                  </p>
                  {order && (
                    <p className="text-sm text-muted-foreground mb-6">
                      Order ID: <span className="font-mono">{order.order_id}</span>
                    </p>
                  )}
                  {order?.gallery_id ? (
                    <Link to={`/galleries/${order.gallery_id}`}>
                      <Button className="w-full">
                        Back to Gallery
                      </Button>
                    </Link>
                  ) : (
                    <Link to={getHomeLink()}>
                      <Button className="w-full">
                        Continue Browsing
                      </Button>
                    </Link>
                  )}
                </motion.div>
              )}

              {/* Failed State */}
              {status === "failed" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <h2 className="text-2xl font-serif mb-2">Payment Failed</h2>
                  <p className="text-muted-foreground mb-6">
                    Unfortunately, your payment could not be processed. 
                    Please try again or contact support if the issue persists.
                  </p>
                  <Link to={order?.gallery_id ? `/galleries/${order.gallery_id}` : getHomeLink()}>
                    <Button className="w-full">
                      Try Again
                    </Button>
                  </Link>
                </motion.div>
              )}

              {/* Error State */}
              {status === "error" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <h2 className="text-2xl font-serif mb-2">Something Went Wrong</h2>
                  <p className="text-muted-foreground mb-6">
                    We could not verify your order. If you were charged, please contact support 
                    with your order details.
                  </p>
                  {orderId && (
                    <p className="text-sm text-muted-foreground mb-6">
                      Reference: <span className="font-mono">{orderId}</span>
                    </p>
                  )}
                  <div className="space-y-3">
                    <Link to={getHomeLink()}>
                      <Button className="w-full">
                        Continue Browsing
                      </Button>
                    </Link>
                    <a href="mailto:support@360lumina.com">
                      <Button variant="outline" className="w-full">
                        Contact Support
                      </Button>
                    </a>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Branding Footer - conditional based on photographer tier */}
      <footer className="mt-12 text-center">
        <BrandingFooter planKey={photographerPlanKey} className="text-sm" />
      </footer>

      {/* Floating branding badge for Starter/Pro tiers */}
      <FloatingBrandingBadge planKey={photographerPlanKey} />
    </div>
  );
};

export default DownloadSuccess;
